import { rotuloTipoAndamento } from '@/shared/utils/rotulosAndamentoArbitral';
import { ehStatusArbitralTerminal } from '@/shared/utils/statusArbitralTerminal';
import type {
  ProcessoArbitralDetailStatus,
  TipoParte,
  StatusAcessoPlataforma,
  StatusNotificacao,
  TipoPendencia,
  PendenciaProcesso,
  StatusPendencia,
  SolicitantePendencia,
  DestinatarioPendencia,
  StatusFase,
  ResponsavelFase,
  TipoEventoAndamento,
  ProcessoArbitralDocumentoCategoria,
} from '../types/arbitralDetailTypes';

const STATUS_ALIASES: Record<string, ProcessoArbitralDetailStatus> = {
  PROCESSO_DISTRIBUIDO: 'PROCESSO_DISTRIBUIDO',
  TRIAGEM_EM_ANDAMENTO: 'TRIAGEM_EM_ANDAMENTO',
  TRIAGEM_CONCLUIDA: 'TRIAGEM_CONCLUIDA',
  EM_CITACAO: 'EM_CITACAO',
  EM_ANDAMENTO: 'EM_ANDAMENTO',
  SENTENCA_EMITIDA: 'SENTENCA_EMITIDA',
  CONCLUIDO: 'CONCLUIDO',
  NEGADO: 'NEGADO',
  CANCELADO: 'CANCELADO',
  // Vocabulário EN legado do detalhe. O core atual envia o enum pt-BR cru (paridade
  // lista×detalhe, ClickUp 86ak86fw7); aliases mantidos só para a janela de deploy core×Vercel.
  // AWAITING_SENTENCE carregava SENTENCA_EMITIDA e era exibido "Em Andamento" — era essa a
  // divergência. IN_DILIGENCE agregava EM_CITACAO e EM_ANDAMENTO (irrecuperável; fica no mais
  // provável). TODO: remover os aliases EN após o deploy do core.
  AWAITING_TRIAGE: 'PROCESSO_DISTRIBUIDO',
  TRIAGE_IN_PROGRESS: 'TRIAGEM_EM_ANDAMENTO',
  AWAITING_PAYMENT: 'TRIAGEM_CONCLUIDA',
  IN_DILIGENCE: 'EM_ANDAMENTO',
  AWAITING_SENTENCE: 'SENTENCA_EMITIDA',
  CLOSED: 'CONCLUIDO',
  DENIED: 'NEGADO',
  CANCELED: 'CANCELADO',
};

export function normalizeArbitralDetailStatus(raw: string): ProcessoArbitralDetailStatus {
  return STATUS_ALIASES[(raw ?? '').toUpperCase()] ?? 'INDEFINIDO';
}

export function getStatusLabel(status: ProcessoArbitralDetailStatus): string {
  const labels: Record<ProcessoArbitralDetailStatus, string> = {
    PROCESSO_DISTRIBUIDO: 'Processo Distribuído',
    TRIAGEM_EM_ANDAMENTO: 'Triagem em Andamento',
    TRIAGEM_CONCLUIDA: 'Triagem Concluída',
    EM_CITACAO: 'Em Citação',
    EM_ANDAMENTO: 'Em Andamento',
    SENTENCA_EMITIDA: 'Sentença Emitida',
    CONCLUIDO: 'Concluído',
    NEGADO: 'Negado',
    CANCELADO: 'Cancelado',
    INDEFINIDO: 'Status não identificado',
  };
  return labels[status] ?? status;
}

export function getStatusVariant(
  status: ProcessoArbitralDetailStatus,
): 'brand' | 'neutral' | 'error' | 'warning' | 'success' | 'info' {
  const variants: Record<
    ProcessoArbitralDetailStatus,
    'brand' | 'neutral' | 'error' | 'warning' | 'success' | 'info'
  > = {
    PROCESSO_DISTRIBUIDO: 'neutral',
    TRIAGEM_EM_ANDAMENTO: 'info',
    TRIAGEM_CONCLUIDA: 'info',
    EM_CITACAO: 'brand',
    EM_ANDAMENTO: 'brand',
    SENTENCA_EMITIDA: 'warning',
    CONCLUIDO: 'success',
    NEGADO: 'error',
    CANCELADO: 'error',
    INDEFINIDO: 'neutral',
  };
  return variants[status] ?? 'neutral';
}

export function getStatusDescription(status: ProcessoArbitralDetailStatus): string {
  const descriptions: Record<ProcessoArbitralDetailStatus, string> = {
    PROCESSO_DISTRIBUIDO: 'Na fila da secretaria, ainda não iniciado',
    TRIAGEM_EM_ANDAMENTO: 'Secretaria analisando os documentos',
    TRIAGEM_CONCLUIDA: 'Documentos confirmados (CPF, nome, cláusula)',
    EM_CITACAO: 'Citando o requerido; árbitro já nomeado',
    EM_ANDAMENTO: 'Despacho, contestação, manifestação e instrução',
    SENTENCA_EMITIDA: 'Sentença emitida; aguardando comprovação de entrega',
    CONCLUIDO: 'Encerrado por trânsito em julgado, desistência ou acordo',
    NEGADO: 'Processo inadmitido pela secretaria',
    CANCELADO: 'Processo cancelado pela parte',
    INDEFINIDO: 'Status ainda não identificado pelo sistema',
  };
  return descriptions[status] ?? '';
}

export function isProcessoEditavel(status: ProcessoArbitralDetailStatus): boolean {
  return status === 'PROCESSO_DISTRIBUIDO';
}

export function getTipoParteLabel(tipo: TipoParte): string {
  const labels: Record<TipoParte, string> = {
    REQUERENTE: 'Requerente',
    REQUERIDO: 'Requerido',
    ADVOGADO_REQUERENTE: 'Advogado do Requerente',
    ADVOGADO_REQUERIDO: 'Advogado do Requerido',
    REPRESENTANTE_REQUERENTE: 'Representante do Requerente',
    REPRESENTANTE_REQUERIDO: 'Representante do Requerido',
  };
  return labels[tipo] ?? tipo;
}

/**
 * A OAB só identifica quem atua como advogado. O backend devolve `oab` em qualquer parte,
 * então partes de outros papéis chegavam com o campo preenchido e a exibição vazava.
 */
export function isParteAdvogado(tipo: TipoParte): boolean {
  return tipo === 'ADVOGADO_REQUERENTE' || tipo === 'ADVOGADO_REQUERIDO';
}

export function getTipoParteVariant(
  tipo: TipoParte,
): 'brand' | 'neutral' | 'error' | 'warning' | 'success' {
  const variants: Record<TipoParte, 'brand' | 'neutral' | 'error' | 'warning' | 'success'> = {
    REQUERENTE: 'brand',
    REQUERIDO: 'neutral',
    ADVOGADO_REQUERENTE: 'brand',
    ADVOGADO_REQUERIDO: 'neutral',
    REPRESENTANTE_REQUERENTE: 'brand',
    REPRESENTANTE_REQUERIDO: 'neutral',
  };
  return variants[tipo] ?? 'neutral';
}

export function getStatusAcessoLabel(status: StatusAcessoPlataforma): string {
  const labels: Record<StatusAcessoPlataforma, string> = {
    CADASTRADO: 'Cadastrado',
    NUNCA_ACESSOU: 'Nunca acessou',
    BLOQUEADO: 'Bloqueado',
    SEM_CADASTRO: 'Sem cadastro',
  };
  return labels[status] ?? status;
}

export function getStatusAcessoVariant(
  status: StatusAcessoPlataforma,
): 'brand' | 'neutral' | 'error' | 'warning' | 'success' {
  const variants: Record<
    StatusAcessoPlataforma,
    'brand' | 'neutral' | 'error' | 'warning' | 'success'
  > = {
    CADASTRADO: 'success',
    NUNCA_ACESSOU: 'neutral',
    BLOQUEADO: 'error',
    SEM_CADASTRO: 'warning',
  };
  return variants[status] ?? 'neutral';
}

export function getNotificacaoResumo(status: StatusNotificacao): {
  label: string;
  variant: 'success' | 'warning' | 'error' | 'neutral';
} {
  const resumo: Record<
    StatusNotificacao,
    { label: string; variant: 'success' | 'warning' | 'error' | 'neutral' }
  > = {
    LIDA: { label: 'Notificação lida', variant: 'success' },
    ENTREGUE: { label: 'Notificada', variant: 'success' },
    PENDENTE: { label: 'Envio pendente', variant: 'warning' },
    NAO_ENVIADA: { label: 'Não notificada', variant: 'neutral' },
    BOUNCE: { label: 'Falha no envio', variant: 'error' },
  };
  return resumo[status] ?? { label: 'Não notificada', variant: 'neutral' };
}

export function getTipoPendenciaLabel(tipo: TipoPendencia): string {
  // TODO(#794): rótulos voltados à PARTE, divergentes dos da Secretaria em
  // `secretaria-arbitral/utils/pendenciasLabels`; promover para `shared/` se produto unificar.
  const labels: Record<TipoPendencia, string> = {
    DOCUMENTACAO: 'Documentação',
    PAGAMENTO: 'Pagamento',
    MANIFESTACAO: 'Manifestação',
    ESCLARECIMENTO: 'Esclarecimento',
    OUTROS: 'Pendência da Secretaria',
    CORRECAO_DADO: 'Correção de dado',
    HABILITACAO_ADVOGADO: 'Habilitação de advogado',
    ASSINATURA: 'Assinatura',
    INFORMACAO: 'Informação',
    CONCLUSO: 'Conclusão ao árbitro',
    CONCLUSO_RESPONDIDO: 'Resposta do árbitro ao concluso',
    TERMO_IMPARCIALIDADE: 'Termo de imparcialidade',
    IMPUGNACAO_ARBITRO: 'Impugnação de árbitro',
    PRAZO_IMPUGNACAO_ARBITRO: 'Prazo para impugnar o árbitro',
    KYC: 'Verificação de identidade',
    REGULARIZACAO_TRIAGEM: 'Regularização de documentação',
    OFICIO_REQUISITORIO: 'Ofício requisitório',
  };
  return labels[tipo] ?? 'Pendência';
}

export function getStatusPendenciaLabel(status: StatusPendencia): string {
  const labels: Record<StatusPendencia, string> = {
    PENDENTE: 'Pendente',
    AGUARDANDO_VALIDACAO: 'Aguardando validação',
    RESOLVIDA: 'Resolvida',
    REJEITADA: 'Rejeitada',
    EXPIRADA: 'Expirada',
    ENCERRADA_PELA_SENTENCA: 'Encerrada pela sentença',
  };
  return labels[status] ?? status;
}

export function getStatusPendenciaVariant(
  status: StatusPendencia,
): 'brand' | 'neutral' | 'error' | 'warning' | 'success' {
  const variants: Record<StatusPendencia, 'brand' | 'neutral' | 'error' | 'warning' | 'success'> = {
    PENDENTE: 'warning',
    AGUARDANDO_VALIDACAO: 'brand',
    RESOLVIDA: 'success',
    REJEITADA: 'error',
    EXPIRADA: 'neutral',
    ENCERRADA_PELA_SENTENCA: 'neutral',
  };
  return variants[status] ?? 'neutral';
}

/**
 * A pendência saiu da fila de quem responde: foi respondida (RESOLVIDA) ou o prazo acabou e o cron
 * do core a fechou (EXPIRADA), ou a sentença a encerrou sem cumprimento.
 * Status que o web ainda não conhece fica do lado das ativas: mostrar
 * a mais é recuperável, sumir com uma pendência viva não é.
 */
export function pendenciaEncerrada(status: StatusPendencia): boolean {
  return status === 'RESOLVIDA' || status === 'EXPIRADA' || status === 'ENCERRADA_PELA_SENTENCA';
}

/** A parte que está vendo a pendência tem algo a fazer nela (responder ou reenviar). */
export function pendenciaAguardaRespostaDaParte(pendencia: PendenciaProcesso): boolean {
  return (
    pendencia.podeResponderPendencia &&
    (pendencia.status === 'PENDENTE' || pendencia.status === 'REJEITADA')
  );
}

export function getSolicitanteLabel(solicitante: SolicitantePendencia): string {
  const labels: Record<SolicitantePendencia, string> = {
    SECRETARIA: 'Secretaria',
    SISTEMA: 'Sistema',
    ARBITRO: 'Árbitro',
  };
  return labels[solicitante] ?? solicitante;
}

export function getDestinatarioPendenciaLabel(destinatario: DestinatarioPendencia): string {
  const labels: Record<DestinatarioPendencia, string> = {
    REQUERENTE: 'Requerente',
    REQUERIDO: 'Requerido',
    ARBITRO: 'Árbitro',
    SECRETARIA: 'Secretaria',
  };
  return labels[destinatario] ?? destinatario;
}

export function getStatusFaseLabel(status: StatusFase): string {
  const labels: Record<StatusFase, string> = {
    CONCLUIDA: 'Concluída',
    EM_ANDAMENTO: 'Em andamento',
    AGUARDANDO: 'Aguardando',
  };
  return labels[status] ?? status;
}

export function getResponsavelFaseLabel(responsavel: ResponsavelFase | null): string {
  if (responsavel === null) return '—';
  const labels: Record<ResponsavelFase, string> = {
    EMPRESA: 'Sua empresa',
    REQUERIDO: 'Parte requerida',
    SECRETARIA: 'Secretaria',
    ARBITRO: 'Árbitro',
    SISTEMA: 'Sistema',
  };
  return labels[responsavel] ?? responsavel;
}

export function getTipoEventoAndamentoLabel(tipo: TipoEventoAndamento): string {
  return rotuloTipoAndamento(tipo);
}

export function formatDiasRestantes(dias: number): {
  texto: string;
  variant: 'error' | 'warning' | 'success' | 'neutral';
} {
  if (dias < 0) {
    return {
      texto: `Vencido há ${Math.abs(dias)} dia${Math.abs(dias) === 1 ? '' : 's'}`,
      variant: 'error',
    };
  }
  if (dias === 0) {
    return { texto: 'Vence hoje', variant: 'error' };
  }
  if (dias <= 3) {
    return {
      texto: `${dias} dia${dias === 1 ? '' : 's'} restante${dias === 1 ? '' : 's'}`,
      variant: 'warning',
    };
  }
  return {
    texto: `${dias} dias restantes`,
    variant: 'success',
  };
}

export function getInitials(nome: string): string {
  const parts = nome.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export function getAutorTipoLabel(tipo: TipoParte | 'SECRETARIA' | 'SISTEMA' | 'ARBITRO'): string {
  if (tipo === 'SECRETARIA') return 'Secretaria';
  if (tipo === 'SISTEMA') return 'Sistema';
  if (tipo === 'ARBITRO') return 'Árbitro';
  return getTipoParteLabel(tipo);
}

export function getCategoriaLabel(categoria: ProcessoArbitralDocumentoCategoria): string {
  const labels: Record<ProcessoArbitralDocumentoCategoria, string> = {
    comprovacao: 'Documentos de Comprovação',
    conflito: 'Documentos do Conflito',
    andamento: 'Anexos de andamento',
  };
  return labels[categoria] ?? categoria;
}

export const isProcessoArbitralEncerrado = (
  status: ProcessoArbitralDetailStatus | undefined,
): boolean => ehStatusArbitralTerminal(status);
